import React, { useState, useEffect } from "react";
import {
  UserCircleIcon,
  ChevronDownIcon,
  XMarkIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Authentication/AuthContext";
import { toast } from "sonner";
import { FiHome } from "react-icons/fi";
import { FaBars, FaMapMarkerAlt } from "react-icons/fa";
import { MdManageAccounts } from "react-icons/md";
import { BookOpenIcon } from "lucide-react";
import { BsQrCodeScan } from "react-icons/bs";

const MobileVendorHeader = ({
  navItems,
  vendorIsApproved,
  userRole,
  setUserRole,
  handleLogoutClick,
  currentLocation,
  setShowLocationModal,
  setIsOpenQrScan,
}) => {
  const navigate = useNavigate();
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const { user } = useAuth();

  const toggleSideMenu = () => {
    setIsSideMenuOpen(!isSideMenuOpen);
    setIsProfileDropdownOpen(false)
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
    setIsSideMenuOpen(false)
  };

  const toggleSidemenuDropdown = (index) => {
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsSideMenuOpen(false);
  };

  const handleRoleSwitch = () => {
    const newRole = userRole === "user" ? "vendor" : "user";
    setUserRole(newRole);
    localStorage.setItem("userRole", newRole);
    setIsSideMenuOpen(false);
    toast.success(`Switched to ${newRole} view`);
    navigate(newRole === "vendor" ? "/vendor/dashboard" : "/LocalActivities");
  };

  const locationSubtext = [currentLocation.area, currentLocation.state]
    .filter(Boolean)
    .join(", ");

  useEffect(() => {
    const storedRole = localStorage.getItem("userRole");
    if (storedRole) {
      setUserRole(storedRole);
    }
  }, []);

  return (
    <header className="bg-white shadow-md py-3 px-4 border-b border-gray-200 lg:hidden">
      <div className="container mx-auto flex items-center justify-between space-x-2">
        {/* Left: Menu + Logo */}
        <div className="flex items-center gap-1">
          <button
            onClick={toggleSideMenu}
            className="text-gray-700 p-2 rounded hover:bg-gray-100 focus:outline-none"
          >
            <FaBars className="w-5 h-5" />
          </button>
          <img
            src="/upswap.png"
            alt="UpSway Logo"
            className="h-6 cursor-pointer"
            onClick={() => navigate("/vendor/dashboard")}
          />
        </div>

        {/* Center: Location */}
        <div
          className="flex-grow flex justify-center items-center gap-2 min-w-0 cursor-pointer"
          onClick={() => setShowLocationModal(true)}
        >
          <FaMapMarkerAlt className="text-gray-500 w-3 h-3" />
          <div className="flex flex-col w-50 text-center">
            <div className="font-semibold text-xs text-gray-800 truncate">
              {currentLocation.city || "Select Location"}
            </div>
            {locationSubtext && (
              <div className="text-[10px] text-gray-500 truncate">
                {locationSubtext}
              </div>
            )}
          </div>
        </div>

        {/* Right: Profile + QR Scan */}
        <div className="flex items-center space-x-2 ml-auto">
          <button
            onClick={() => setIsOpenQrScan(true)}
            className="bg-orange-500 text-white p-2 rounded-full hover:bg-orange-600 transition"
          >
            <BsQrCodeScan className="w-3 h-3" />
          </button>

          <div className="relative cursor-pointer text-gray-600 hover:text-black flex items-center">
            <button
              onClick={toggleProfileDropdown}
              className="flex items-center gap-1"
            >
              <UserCircleIcon className="w-6 h-6" />
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>

            {isProfileDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                <button
                  onClick={() => {
                    navigate("/BasicInfo");
                    setIsProfileDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Vendor Profile
                </button>
                <button
                  onClick={() => {
                    navigate("/mySalesDeal");
                    setIsProfileDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  My Sales
                </button>
                <button
                  onClick={() => {
                    navigate("/Settings");
                    setIsProfileDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Settings
                </button>
                <button
                  onClick={() => {
                    navigate("/ContactSupport");
                    setIsProfileDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Help & Support
                </button>
                <button
                  onClick={() => toast.info("Notifications coming soon!")}
                  className="relative block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Notification
                  <span className="absolute top-2 right-3 h-2 w-2 rounded-full bg-red-500 border border-white"></span>
                </button>
                <button
                  onClick={handleLogoutClick}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-gray-800 bg-opacity-50 z-40 transition-opacity ${
          isSideMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleSideMenu}
      ></div>

      {/* Side Menu */}
      <div
        className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg z-50 transform transition-transform ${
          isSideMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Vendor Menu</h2>
          <button onClick={toggleSideMenu} className="text-gray-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation Accordion */}
        <nav className="mt-4">
          <ul className="space-y-1 px-2">
            <li>
              <button
                onClick={() => handleNavigation("/vendor/dashboard")}
                className="flex items-center gap-3 w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <FiHome className="h-5 w-5" />
                Dashboard
              </button>
            </li>

            {navItems.map((item, index) => (
              <li key={index}>
                <button
                  onClick={() => toggleSidemenuDropdown(index)}
                  className="flex items-center justify-between w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </div>
                  <ChevronDownIcon
                    className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                      openDropdownIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openDropdownIndex === index && (
                  <ul className="pl-8 pt-1 pb-2 space-y-1">
                    {item.dropdownItems.map((dItem) => (
                      !vendorIsApproved && dItem?.id === 2 ? null : (
                        <li key={dItem.name}>
                          <button
                            onClick={() => handleNavigation(dItem.path)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
                          >
                            {dItem.name}
                          </button>
                        </li>
                      )
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Role Switcher */}
        {vendorIsApproved && (
          <div className="border-t border-gray-200 mt-4 pt-4 px-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                Switch to {userRole === "user" ? "Vendor" : "User"} View
              </span>
              <button
                onClick={handleRoleSwitch}
                className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-3 py-1 rounded"
              >
                {userRole === "user" ? "Vendor" : "User"}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default MobileVendorHeader;